<template>
  <ValidationObserver ref="rescheduleInterview">
    <DialogFull :data="dialogSettings" @close="close">
      <template v-slot:dialog.body>
        <div class="reschedule-interview container col-lg-8 col-sm-12">
          <div class="mt-5">
            <h1 class="mb-5">Reschedule Interview</h1>
            <div>
              <div class="d-flex align-center mb-2">
                <v-icon class="interview-icon" large>
                  mdi-clipboard-text-outline
                </v-icon>
                <div clas="d-flex flex-column">
                  <h2>
                    {{ interviewFormat.interviewName | capitalize }}
                  </h2>
                  <div class="strikethrough">
                    {{ interviewDateTimeRange }}
                  </div>
                </div>
              </div>
              Why do you want to reschedule the interview?
              <ValidationProvider
                v-slot="{ errors }"
                name="reason"
                rules="required"
              >
                <v-textarea
                  :error-messages="showValidationErrors ? errors : ''"
                  v-model="reason"
                  height="200"
                  color="primary"
                  placeholder="Add comment..."
                />
              </ValidationProvider>
              <ScheduleInterviewSlotSelection
                :interviewFormatId="interviewFormatId"
                :interviewers="interviewersUserIds"
                :showValidationErrors="showValidationErrors"
                @onSelectSlot="val => (selectedSlot = val)"
              />
            </div>
          </div>
        </div>
      </template>
      <template v-slot:dialog.action>
        <PrimaryButton @click.native="handleSubmit" :loading="isLoading">
          Reschedule Interview
        </PrimaryButton>
      </template>
    </DialogFull>
  </ValidationObserver>
</template>

<script>
import DialogFull from "@/components/common/DialogFull";
import PrimaryButton from "@/components/common/Button/PrimaryButton";
import { INTERVIEWS_NAMESPACE } from "@/store/modules/bookings/interviews";
import { RESCHEDULE_SLOT } from "@/store/modules/bookings/interviews/actions";
import { createNamespacedHelpers } from "vuex";
import { copyState } from "@/utils/helpers";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { getInterviewDateTimeRange } from "@/views/bookings/BookingInterviews/modules/utils";
import ScheduleInterviewSlotSelection from "@/views/bookings/BookingInterviews/components/dialogs/ScheduleInterview/components/ScheduleInterviewSlotSelection";
import { getInterviewParticipants } from "@/views/bookings/BookingInterviews/modules/utils";
import { map, first } from "lodash";

const {
  mapActions: mapInterviewActions,
  mapState: mapInterviewState
} = createNamespacedHelpers(INTERVIEWS_NAMESPACE);

export default {
  name: "RescheduleInterview",
  components: {
    PrimaryButton,
    DialogFull,
    ValidationObserver,
    ValidationProvider,
    ScheduleInterviewSlotSelection
  },
  props: {
    isOpen: Boolean
  },
  data() {
    return {
      showValidationErrors: false,
      isLoading: false,
      selectedSlot: null,
      reason: ""
    };
  },
  computed: {
    interviewDateTimeRange() {
      return getInterviewDateTimeRange(this.currentInterviewSlot);
    },
    interviewFormat() {
      return this.currentInterviewSlot.interviewFormat;
    },
    interviewFormatId() {
      return this.interviewFormat.id;
    },
    dialogSettings() {
      return {
        dialog: this.isOpen,
        title: "Reschedule Interview"
      };
    },
    ...mapInterviewState(copyState(["currentInterviewSlot"])),
    interviewersUserIds() {
      return map(
        getInterviewParticipants(this.currentInterviewSlot, "interviewer"),
        ({ user }) => user.id
      );
    },
    candidateUserId() {
      return first(
        getInterviewParticipants(this.currentInterviewSlot, "candidate")
      ).user.id;
    },
    currentRouteName() {
      return this.$router.currentRoute.name;
    }
  },
  methods: {
    ...mapInterviewActions({
      rescheduleSlot: RESCHEDULE_SLOT
    }),
    async handleSubmit() {
      const isValid = await this.$refs.rescheduleInterview.validate();
      this.showValidationErrors = true;
      if (isValid && this.selectedSlot) {
        const {
          candidateAdditionalDetails,
          interviewerAdditionalDetails,
          location
        } = this.currentInterviewSlot;
        this.isLoading = true;
        const id = await this.rescheduleSlot({
          interviewFormatId: this.interviewFormatId,
          data: {
            slotToAdd: {
              startTime: this.selectedSlot.startTime,
              endTime: this.selectedSlot.endTime,
              interviewers: map(this.interviewersUserIds, id => ({ id })),
              candidate: {
                id: this.candidateUserId
              },
              interviewerAdditionalDetails,
              candidateAdditionalDetails,
              location
            },
            slotToRemove: this.currentInterviewSlot.id,
            reason: this.reason
          }
        });
        this.loadToNewInterviewSlot(id);
        this.close();
        this.isLoading = false;
      }
    },
    close() {
      this.$emit("close");
    },
    loadToNewInterviewSlot(interviewId) {
      this.$router.push({
        name: this.currentRouteName,
        params: { interviewId }
      });
    }
  }
};
</script>
